import Button from '@components/Button';
import SortableList from '@components/SortableList';
import SvgIcon from '@components/SvgIcon';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import IconBatch from '@components/icons/IconBatch';
import IconGroup from '@components/icons/IconGroup';
import useToggle from '@hooks/useToggle';
import { $sceneMapApi } from '@stores/dataStores';
import { $editingHotspotApi } from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { FeatureType, HSIconType, type Hotspot, type SceneData } from '@types';
import arrayMove from '@utils/arrayMove';
import { nanoid } from 'nanoid';
import { Scrollbars } from 'react-custom-scrollbars';
import type { SortEnd } from 'react-sortable-hoc';
import styled from 'styled-components';
import { customButtonDefault } from '../../edit';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import {
  HOTSPOT_MAX,
  TITLE_SETTING_DEFAULT,
  hotspotIconDefault,
  hsRecentSettings,
  krpAddHotspot,
  reOrderHotspots,
  toggleHotspotSelected,
} from '../index';
import HotspotItem from './HotspotItem';

interface Props {
  scene: SceneData;
  onBatchClick: () => void;
  onGroupClick: () => void;
  onLayerClick: () => void;
}

export function HotspotList({
  scene,
  onBatchClick,
  onGroupClick,
  onLayerClick,
}: Props) {
  const hotspotList = scene.hotspot ?? [];
  const loading = useToggle(false);
  const isMax = hotspotList.length >= HOTSPOT_MAX;

  const addHotspot = () => {
    loading.on();
    const id = `h_${nanoid()}`;
    const zOrder = hotspotList?.length ?? 0;
    const view = MainPlayer.get()?.getView();
    const newHotspot: Hotspot = {
      id,
      zOrder,
      icon: hotspotIconDefault({
        type: HSIconType.system,
        ath: view?.hlookat,
        atv: view?.vlookat,
      }),
      title: '',
      showTitle: 1,
      titleSetting: hsRecentSettings.titleSetting || TITLE_SETTING_DEFAULT,
      data: {
        ...customButtonDefault(FeatureType.scene),
        ...hsRecentSettings.featureScene, // 使用最近一次的设置
      },
      visible: 1,
      locked: 0,
    };
    krpAddHotspot(newHotspot);
    $editingHotspotApi.set({
      hotspot: newHotspot,
      sceneId: scene.id,
      isNew: true,
      sceneType: scene.type,
    });
    toggleHotspotSelected({ id, isOn: true });
    loading.off();
  };

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.hotspot = arrayMove({
          array: draft.hotspot ?? [],
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        }).map((x, i) => ({ ...x, zOrder: i }));
      },
    });
    reOrderHotspots();
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex">
            <Title>热点</Title>
            <Tip className="ml-10 self-end text-base">
              {`${hotspotList.length}/${HOTSPOT_MAX}`}
            </Tip>
          </div>
        }
        btns={
          <>
            <TipWrapper
              className="mr-10"
              text="热点显示范围"
              placement="bottom"
              size="sm"
            >
              <IconLayer
                className="focus:outline-none"
                onClick={onLayerClick}
              />
            </TipWrapper>
            <TipWrapper
              className="mr-10"
              text="热点分组设置"
              placement="bottom"
              size="sm"
            >
              <IconGroup
                className="focus:outline-none"
                onClick={onGroupClick}
              />
            </TipWrapper>
            <TipWrapper text="批量操作" placement="bottom" size="sm">
              <IconBatch
                className="focus:outline-none"
                onClick={onBatchClick}
              />
            </TipWrapper>
          </>
        }
      />
      <Button
        size="lg"
        className="mb-10 w-full"
        disabled={loading.isOn || isMax}
        onClick={addHotspot}
      >
        添加热点
      </Button>
      {hotspotList.length > 0 ? (
        <div className="relative flex-1">
          <SortableList
            axis="y"
            lockAxis="y"
            distance={10}
            onSortEnd={handleSortEnd}
          >
            <Scrollbars>
              {hotspotList.map((x, i) => (
                <HotspotItem
                  index={i}
                  key={x.id}
                  data={x}
                  sceneId={scene.id}
                  listIndex={i}
                  isMax={isMax}
                  sceneType={scene.type}
                />
              ))}
            </Scrollbars>
          </SortableList>
        </div>
      ) : (
        <>
          <Tip className="mt-5 text-center text-base">
            {`每个场景最多可添加${HOTSPOT_MAX}个热点`}
          </Tip>
          <Tip className="mt-5 text-center text-base">
            添加后支持上下拖拽调整热点顺序
          </Tip>
        </>
      )}
    </>
  );
}

const IconLayer = styled(SvgIcon).attrs({
  children: ({ color }: { color: string }) => (
    <path
      d="M8 0q-.577 0-1.126.072l.26 1.983Q7.554 2 8 2q.456 0 .884.058L9.151.075Q8.589 0 8 0ZM4.677.699q-1.021.468-1.907 1.244l1.318 1.504q.666-.584 1.422-.93L4.677.7Zm8.571 1.26q-.883-.778-1.903-1.25l-.839 1.816q.755.349 1.42.935l1.322-1.5ZM1.27 3.652q-.643.973-.961 2.083l1.922.551q.234-.815.708-1.532L1.269 3.652Zm14.43 2.107q-.314-1.111-.954-2.086L13.073 4.77q.47.718.702 1.534l1.924-.545ZM12 8q0 1.657-1.172 2.828Q9.657 12 8 12q-1.657 0-2.828-1.172Q4 9.657 4 8q0-1.657 1.172-2.828Q6.343 4 8 4q1.657 0 2.828 1.172Q12 6.343 12 8Zm-1 0q0-1.243-.879-2.121Q9.243 5 8 5q-1.243 0-2.121.879Q5 6.757 5 8q0 1.243.879 2.121Q6.757 11 8 11q1.243 0 2.121-.879Q11 9.243 11 8ZM9 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM0 8q0 1.182.303 2.249l1.924-.547Q2 8.903 2 8v-.007l-2-.001V8Zm15.694 2.257Q16 9.187 16 8h-2q0 .907-.229 1.708l1.923.549Zm-13.35 3.4q.202.203.414.39l1.32-1.503q-.163-.143-.32-.301-.48-.48-.827-1.008l-1.671 1.1q.46.698 1.083 1.322Zm11.313 0q.62-.621 1.079-1.316l-1.67-1.1q-.346.525-.823 1.002-.16.16-.327.306l1.32 1.503q.214-.189.42-.395Zm-8.995 1.637q1.034.476 2.195.632l.265-1.983q-.863-.115-1.623-.465l-.837 1.816Zm4.472.633q1.162-.154 2.196-.63l-.835-1.817q-.76.35-1.623.464l.262 1.983Z"
      fillRule="evenodd"
      fill={color}
    />
  ),
})``;
